import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Package, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Plus,
  Filter
} from "lucide-react";

const MyShoots = () => {
  const [shoots, setShoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchShoots();
  }, []);

  const fetchShoots = async () => {
    try {
      const response = await axios.get(`${API}/shoots`, { withCredentials: true });
      setShoots(response.data);
    } catch (error) {
      toast.error("Failed to load shoots");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-green-600 bg-green-50";
      case "processing": return "text-blue-600 bg-blue-50";
      case "queued": return "text-yellow-600 bg-yellow-50";
      case "failed": return "text-red-600 bg-red-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-4 h-4" />;
      case "processing": return <Clock className="w-4 h-4 animate-spin" />;
      case "queued": return <Clock className="w-4 h-4" />;
      case "failed": return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const filteredShoots = filter === "all" 
    ? shoots 
    : shoots.filter(s => s.status === filter);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="my-shoots-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
              My Shoots
            </h1>
            <p className="text-neutral-600 mt-1">
              {shoots.length} total shoots
            </p>
          </div>
          <Link to="/new-shoot">
            <Button className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-full px-6">
              <Plus className="w-4 h-4 mr-2" />
              New AI Shoot
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-neutral-500" />
          {["all", "completed", "processing", "queued", "failed"].map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-indigo-600" : ""}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Shoots Grid */}
        {filteredShoots.length === 0 ? (
          <Card className="border-neutral-100">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-neutral-400" />
              </div>
              <p className="text-neutral-600 mb-4">
                {filter === "all" ? "No shoots yet. Create your first AI shoot!" : `No ${filter} shoots found.`}
              </p>
              {filter === "all" && (
                <Link to="/new-shoot">
                  <Button className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Create AI Shoot
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShoots.map((shoot) => (
              <Link key={shoot.shoot_id} to={`/shoots/${shoot.shoot_id}`}>
                <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group h-full">
                  <CardContent className="p-0">
                    <div className="relative">
                      <img 
                        src={shoot.sku?.uploaded_image_url || "https://placehold.co/400x300/e5e5e5/a3a3a3?text=SKU"}
                        alt={shoot.sku?.sku_name}
                        className="w-full aspect-[4/3] object-cover rounded-t-xl"
                      />
                      <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(shoot.status)}`}>
                        {getStatusIcon(shoot.status)}
                        <span className="capitalize">{shoot.status}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-neutral-900 mb-1 truncate">
                        {shoot.sku?.sku_name || "Unnamed SKU"}
                      </h3>
                      <p className="text-sm text-neutral-500 mb-3">
                        {shoot.model?.name} • {shoot.background?.name}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-neutral-500">
                          <span>{shoot.assets?.filter(a => a.asset_type === "image").length || 0} images</span>
                          {shoot.assets?.some(a => a.asset_type === "video") && (
                            <span>• 1 video</span>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-indigo-600 transition-colors" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyShoots;
